/**
 * Analytics hub navigation — child routes shown in the nested sub-sidebar.
 */

import type { AuthUser } from '../auth/authService';
import { canAccessPage } from './permissions';

export const ANALYTICS_BASE_PATH = '/analytics';

/** Routes that use the analytics sub-sidebar but live outside /analytics/* */
const ANALYTICS_LEGACY_PATH_PREFIXES = [
  '/critical-results-dashboard',
  '/escalation-monitor',
  '/acknowledgement-audit-trail',
] as const;

export interface AnalyticsChildItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  requiredRoles?: string[];
  /** When true, child stays active for nested routes under `path` (e.g. drill-down). */
  matchPrefix?: boolean;
}

export const ANALYTICS_CHILDREN: AnalyticsChildItem[] = [
  {
    id: 'collection-reconciliation',
    label: 'Billing reconciliation',
    path: '/analytics/collection-reconciliation',
    icon: 'Receipt',
    requiredRoles: ['ris-admin'],
  },
  {
    id: 'statewide-kpis',
    label: 'Statewide KPIs',
    path: '/analytics/statewide-kpis',
    icon: 'BarChart2',
    // Super-admin only — matches PAGE_PERMISSIONS and backend requireSuperAdmin.
    requiredRoles: ['ris-super-admin'],
  },
  {
    id: 'operational',
    label: 'Operational dashboard',
    path: '/analytics/operational',
    icon: 'Activity',
    requiredRoles: ['ris-admin', 'ris-hub-admin', 'ris-radiology-manager'],
  },
  {
    id: 'ai-dashboard',
    label: 'AI results',
    path: '/analytics/ai-dashboard',
    icon: 'Sparkles',
    requiredRoles: ['ris-super-admin', 'ris-admin'],
  },
  {
    id: 'report-delivery',
    label: 'Report delivery',
    path: '/analytics/report-delivery',
    icon: 'Clock',
    requiredRoles: ['ris-admin'],
  },
  {
    id: 'peer-review',
    label: 'Peer review',
    path: '/analytics/peer-review',
    icon: 'ClipboardCheck',
    requiredRoles: ['ris-admin', 'ris-radiologist'],
  },
  {
    id: 'critical-results-dashboard',
    label: 'Critical results',
    path: '/critical-results-dashboard',
    icon: 'Siren',
    requiredRoles: ['ris-admin'],
    matchPrefix: true,
  },
  {
    id: 'acknowledgement-audit-trail',
    label: 'Ack. audit trail',
    path: '/acknowledgement-audit-trail',
    icon: 'ShieldCheck',
    requiredRoles: ['ris-admin', 'ris-quality-manager'],
  },
  {
    id: 'escalation-monitor',
    label: 'Escalation monitor',
    path: '/escalation-monitor',
    icon: 'BellRing',
    requiredRoles: ['ris-admin'],
  },
  {
    id: 'notification-delivery',
    label: 'Notification delivery',
    path: '/analytics/notification-delivery',
    icon: 'Send',
    requiredRoles: ['ris-admin'],
  },
];

export function isAnalyticsPath(pathname: string): boolean {
  if (pathname === ANALYTICS_BASE_PATH || pathname.startsWith(`${ANALYTICS_BASE_PATH}/`)) {
    return true;
  }
  return ANALYTICS_LEGACY_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isAnalyticsChildActive(pathname: string, child: AnalyticsChildItem): boolean {
  if (child.matchPrefix) {
    return pathname === child.path || pathname.startsWith(`${child.path}/`);
  }
  return pathname === child.path;
}

/** First analytics child the user may open (for the main sidebar Analytics icon target). */
export function getFirstAccessibleAnalyticsPath(user: AuthUser | null): string {
  for (const child of ANALYTICS_CHILDREN) {
    if (canAccessPage(user, child.path)) {
      return child.path;
    }
  }
  return ANALYTICS_CHILDREN[0].path;
}

export const ANALYTICS_SIDEBAR_WIDTH = 220;
export const APP_SIDEBAR_WIDTH = 72;
